/**
 * Copy trading as the server and the app both know it. A follower sets money aside to mirror another
 * user's trades; that money behaves like a SUB-ACCOUNT inside the follower's own brokerage account:
 * its own cash, its own positions and its own average costs, never mixed with what the follower holds
 * of the same stock. Nothing of it is stored as a balance — cash and positions are DERIVED from the
 * copy's ledger (foldLedger), so the book can always be re-read from its own history.
 */
import { z } from 'zod'
import type { BrokerName } from './brokers'

/* ── settings of one copy (the follower's own limits) ─────────────────── */

export const CopySettingsSchema = z
  .object({
    /** Never chase: a buy is skipped when the price is this far above what the leader paid (percent). */
    slippagePct: z.number().min(0.1).max(5),
    /** No single stock above this share of the copy's value (percent). */
    maxPositionPct: z.number().min(5).max(100),
    /** Stop copying when the copy's value is down this much of the money put in (percent); null = never. */
    stopAtPct: z.number().min(1).max(95).nullable(),
    /** What a stop does with the shares: sell them, or keep them as the follower's own. */
    onStop: z.enum(['sell', 'keep']),
  })
  .strict()
export type CopySettings = z.infer<typeof CopySettingsSchema>
export const CopySettingsPatchSchema = CopySettingsSchema.partial()
export const DEFAULT_COPY_SETTINGS: CopySettings = { slippagePct: 0.5, maxPositionPct: 25, stopAtPct: 40, onStop: 'sell' }

/** The least a copy can be started with or added to (USD). */
export const COPY_MIN_AMOUNT = 10
/** An order worth less than this is not sent (USD). */
export const COPY_MIN_ORDER = 1

export type CopyStatus = 'active' | 'paused' | 'stopping' | 'stopped'
export type CopyStopMode = 'sell' | 'keep'
export type CopyLedgerKind = 'deposit' | 'withdraw' | 'buy' | 'sell' | 'release' | 'note'
export type CopyIntentStatus = 'planned' | 'queued' | 'sent' | 'filled' | 'partial' | 'skipped' | 'failed' | 'rehearsed'

/* ── money and share arithmetic ───────────────────────────────────────── */

/** Dollars to the cent. */
export const cents = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100
/** Shares to the millionth (Robinhood's fraction). */
export const shares6 = (n: number): number => Math.round(n * 1e6) / 1e6
/** Shares rounded DOWN to the millionth — a size never rounds up into money that is not there. */
export const floorShares = (n: number): number => Math.floor(n * 1e6 + 1e-6) / 1e6
export const fmtCopyUsd = (n: number): string => `${n < 0 ? '−' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
export const fmtCopyQty = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(6).replace(/0+$/, '').replace(/\.$/, ''))

/* ── the ledger and its fold ──────────────────────────────────────────── */

/** One line of a copy's book. `amount` is the signed effect on the copy's cash (fees included). */
export interface CopyLedgerEntry {
  kind: CopyLedgerKind
  symbol: string | null
  qty: number | null
  price: number | null
  amount: number
}

export interface CopyBookPosition {
  symbol: string
  qty: number
  /** Average price paid per share still held. */
  avg: number
}

/** What a copy holds, as its ledger says. */
export interface CopyBook {
  cash: number
  positions: CopyBookPosition[]
  /** Money put in and not taken out again: deposits − withdrawals (released shares count as taken out). */
  putIn: number
  deposited: number
  withdrawn: number
}

/**
 * Fold a ledger (oldest first) into the copy's cash and positions. Cash is summed in whole cents and
 * shares in millionths, so nothing drifts. A buy moves the average cost; a sale reduces the shares and
 * leaves the average; a release hands shares to the follower's own holdings — no cash moves, and what
 * they were worth (the row's price) counts as taken out, so keeping shares is not booked as a loss.
 */
export function foldLedger(rows: readonly CopyLedgerEntry[]): CopyBook {
  let cash = 0
  let deposited = 0
  let withdrawn = 0
  const pos = new Map<string, { micro: number; avg: number }>()
  for (const r of rows) {
    const amount = Math.round((r.amount ?? 0) * 100)
    const micro = Math.round((r.qty ?? 0) * 1e6)
    switch (r.kind) {
      case 'deposit':
        cash += amount
        deposited += amount
        break
      case 'withdraw':
        cash += amount
        withdrawn -= amount
        break
      case 'buy': {
        cash += amount
        if (!r.symbol || micro <= 0) break
        const p = pos.get(r.symbol) ?? { micro: 0, avg: 0 }
        p.avg = p.micro ? (p.avg * p.micro + (r.price ?? 0) * micro) / (p.micro + micro) : (r.price ?? 0)
        p.micro += micro
        pos.set(r.symbol, p)
        break
      }
      case 'sell':
      case 'release': {
        if (r.kind === 'sell') cash += amount
        const p = r.symbol ? pos.get(r.symbol) : undefined
        if (!p) break
        // A copy can never hold less than nothing: a sale past its shares takes what is there.
        const out = Math.min(p.micro, micro)
        // Released shares are money taken out in kind: what they were worth then no longer counts as put in.
        if (r.kind === 'release') withdrawn += Math.round((out / 1e6) * (r.price ?? p.avg) * 100)
        p.micro -= out
        if (!p.micro) pos.delete(r.symbol!)
        break
      }
      case 'note':
        break
    }
  }
  return {
    cash: cash / 100,
    positions: [...pos].map(([symbol, p]) => ({ symbol, qty: p.micro / 1e6, avg: p.avg })),
    putIn: (deposited - withdrawn) / 100,
    deposited: deposited / 100,
    withdrawn: withdrawn / 100,
  }
}

/* ── what the app shows ───────────────────────────────────────────────── */

export interface CopyPositionView {
  symbol: string
  name?: string
  qty: number
  avg: number
  last: number
  /** Today's gain or loss on these shares. */
  day: number
  value: number
  gain: number
  gainPct: number
  /** No live price was known: `last` is the average cost standing in for it. */
  stale?: boolean
}

export interface CopyView {
  id: string
  leader: { id: string; name: string }
  status: CopyStatus
  since: number
  /** Money put in and not taken out again. */
  putIn: number
  cash: number
  /** Cash + the shares at the last price. */
  value: number
  day: number
  dayPct: number
  /** Value − money put in. */
  gain: number
  /** That gain against everything ever put in. */
  gainPct: number
  positions: CopyPositionView[]
  settings: CopySettings
  /** false = rehearsal: the server plans every order and sends none. */
  live: boolean
  stopReason?: string
  /** The follower's own broker this copy trades at. */
  broker: BrokerName
  deposited: number
  withdrawn: number
}

export interface CopyIntentView {
  id: string
  at: number
  updatedAt: number
  symbol: string
  side: 'buy' | 'sell'
  status: CopyIntentStatus
  reason: string | null
  detail: string | null
  /** Dollars planned (a buy) or sold. */
  notional: number | null
  qty: number | null
  /** What the leader's own fill cost per share. */
  refPrice: number | null
  filledQty: number
  avgPrice: number | null
}

export interface CopyLogLine {
  at: number
  /** A ledger kind, or an order's state. */
  kind: CopyLedgerKind | CopyIntentStatus
  text: string
}

export type CopyDetail = CopyView & {
  /** The ledger and the orders, newest first, as plain sentences. */
  log: CopyLogLine[]
  intents: CopyIntentView[]
}

export interface CopyOverview {
  /** false = rehearsal on this server. */
  live: boolean
  /** My own switch: people I invited may copy my trades. */
  allow: boolean
  /** People I am allowed to copy. */
  leaders: { id: string; name: string; copying: boolean }[]
  copies: CopyView[]
  /** People copying me — names and dates only, never amounts. */
  followers: { name: string; since: number; status: CopyStatus }[]
}

export interface CopyConsent {
  version: string
  text: string
}
